import { Injectable } from '@angular/core';
import { CanActivate, CanActivateChild, ActivatedRouteSnapshot, RouterStateSnapshot, Router, UrlTree } from '@angular/router';
import { InstanceService } from './instance.service';

@Injectable({ providedIn: 'root' })
export class InstanceFeatureGuard implements CanActivate, CanActivateChild {
  constructor(private instance: InstanceService, private router: Router) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree {
    return this.check(state.url);
  }

  canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree {
    return this.check(state.url);
  }

  private check(url: string): boolean | UrlTree {
    const cfg = this.instance.getConfig();

    // Alt-layout URL aliases: keep old URLs working but always redirect to the alt-layout friendly ones.
    if (this.instance.isAltLayout) {
      const [pathOnly, query] = url.split('?');
      const qs = query ? `?${query}` : '';

      if (pathOnly === '/pages/clients' || pathOnly.startsWith('/pages/clients/')) {
        return this.router.parseUrl(`/pages/locations${pathOnly.substring('/pages/clients'.length)}${qs}`);
      }

      if (pathOnly === '/pages/prospects' || pathOnly.startsWith('/pages/prospects/')) {
        return this.router.parseUrl(`/pages/archived-locations${pathOnly.substring('/pages/prospects'.length)}${qs}`);
      }

      const clientDetailsMatch = pathOnly.match(/^\/pages\/client-details\/([^\/]+)$/);
      if (clientDetailsMatch?.[1]) {
        return this.router.parseUrl(`/pages/location-details/${clientDetailsMatch[1]}${qs}`);
      }

      const prospectDetailsMatch = pathOnly.match(/^\/pages\/prospect-details\/([^\/]+)$/);
      if (prospectDetailsMatch?.[1]) {
        return this.router.parseUrl(`/pages/archived-location-details/${prospectDetailsMatch[1]}${qs}`);
      }
    }

    const hiddenPrefixes = cfg?.features?.hiddenRoutePrefixes ?? [];
    if (!hiddenPrefixes.length) return true;

    const pathOnly = url.split('?')[0];

    // This guard is attached to the `/pages` route; normalize to the child path under `/pages`.
    const normalized = pathOnly.startsWith('/pages') ? pathOnly.substring('/pages'.length) || '/' : pathOnly;

    const isHidden = hiddenPrefixes.some(prefix => {
      if (!prefix) return false;
      const p = prefix.startsWith('/') ? prefix : `/${prefix}`;
      return normalized === p || normalized.startsWith(`${p}/`) || normalized.startsWith(p);
    });

    if (isHidden) {
      return this.router.parseUrl('/pages/dashboard');
    }

    return true;
  }
}

